// Where the interface language lives before (and beside) the profile row.
// Pure functions over injected inputs so the resolution order is testable.

import { DEFAULT_LOCALE, isLocale, normalizeLocale, pickLocale, type Locale } from '@quoter/shared/locale'

import { getProduct } from '../config'

/** Per product, although localStorage is already per origin: two products on one dev host must not share it. */
export function localeStorageKey(): string {
  return `${getProduct().id}.locale`
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

function safeStorage(): StorageLike | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function readStoredLocale(storage: StorageLike | null = safeStorage()): Locale | null {
  try {
    const value = storage?.getItem(localeStorageKey())
    return isLocale(value) ? value : null
  } catch {
    return null
  }
}

export function writeStoredLocale(locale: Locale, storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.setItem(localeStorageKey(), locale)
  } catch {
    // Private mode or blocked storage: the choice just does not survive a reload.
  }
}

/** `?lang=pt` — the bridge from the landing page and from Stripe's return URLs. */
export function localeFromSearch(search: string): Locale | null {
  return normalizeLocale(new URLSearchParams(search).get('lang'))
}

export type InitialLocale = {
  locale: Locale
  /** True when the value came from a deliberate choice (URL or storage), not the browser. */
  explicit: boolean
}

/** Resolution order: `?lang=` → localStorage → browser languages → default. */
export function resolveInitialLocale(input: {
  search: string
  stored: Locale | null
  navigatorLanguages: readonly string[]
}): InitialLocale {
  const fromSearch = localeFromSearch(input.search)
  if (fromSearch) return { locale: fromSearch, explicit: true }
  if (input.stored) return { locale: input.stored, explicit: true }
  const fromBrowser = pickLocale(input.navigatorLanguages)
  return { locale: fromBrowser ?? DEFAULT_LOCALE, explicit: false }
}
